import type { DesignBoardWithDetailedPinsFragment } from 'generated/graphql';
import { useUploadPin } from '../../hook/use-upload-pin';
import { Flex, useStyles } from '@archipro-design/aria';
import FilterBarAvatars from '~/modules/design-board/component/board-detail/FilterBarAvatars';
import EditButton from '~/modules/design-board/component/board-detail/filter-bar-options/EditButton';
import * as S from '~/modules/design-board/component/board-detail/BoardDetailFilter.style';
import CommentButton from './filter-bar-options/CommentButton';
import ShareButton from './filter-bar-options/ShareButton';
import SubscribeToBoard from './SubscribeToBoard';
import LeaveBoard from './filter-bar-options/LeaveBoard';
import { SHOW_COMMENT } from '../../config/design-board-config';
import { useUser } from '@modules/user';

interface FilterBarProps {
    board: DesignBoardWithDetailedPinsFragment;
}

const FilterBar = ({ board }: FilterBarProps) => {
    const { css } = useStyles();
    const { uploadButton } = useUploadPin({ mobile: false });
    const user = useUser();
    const isLoggedIn = user.__typename === 'Me';
    const commentAccess =
        board.Permission.CanAddComments || board.Permission.CanViewComments;

    const publicBoardOptions = (
        <>
            {isLoggedIn && board.CanEdit && <FilterBarAvatars board={board} />}
            {!board.IsOwner && (
                <SubscribeToBoard
                    isFollower={board.IsFollower}
                    isCollaborator={board.IsCollaborator}
                    board={board}
                />
            )}
            {board.CanEdit && <ShareButton />}
            {board.CanEdit && <EditButton board={board} />}
            {board.Permission.CanUploadFile &&
                uploadButton(css(S.UploadButton))}
        </>
    );
    const privateBoardOptions = (
        <>
            {board.CanEdit && <FilterBarAvatars board={board} />}
            {board.IsCollaborator && (
                <LeaveBoard designBoardId={board.ID} boardTitle={board.Title} />
            )}
            {board.CanEdit && <ShareButton />}
            {board.CanEdit && <EditButton board={board} />}
            {/* Reimplement comments when they are created */}
            {/* https://archipro.atlassian.net/browse/PLAT-3937 */}
            {SHOW_COMMENT &&
                commentAccess &&
                board.Permission.CanAddComments && <CommentButton />}
            {board.Permission.CanUploadFile &&
                uploadButton(css(S.UploadButton))}
        </>
    );

    return (
        <Flex className={css(S.FilterBarButtons)} column={false}>
            {board.Public ? publicBoardOptions : privateBoardOptions}
        </Flex>
    );
};

export default FilterBar;
